import React, { useState, lazy, Suspense } from 'react';

// Hooks
import useCustomerLogin from '@src/hooks/useCustomerLogin';
import useCustomerAccessToken from '@src/hooks/useCustomerAccessToken';

// Comps
const AccountForgotPassword = lazy(
    () => import('@project-stories/ecommerce/Account/AccountForgotPassword/AccountForgotPassword')
);
import { TextField } from "@src/stories/elements/TextField/TextField";
import {Link} from '@arctheme-components/elements/Link/Link';
import {Paragraph} from '@arctheme-components/elements/Paragraph/Paragraph';
import ButtonSpinner from '@src/stories/elements/ButtonSpinner/ButtonSpinner';
import {Checkbox} from "@project-stories/elements/Checkbox/Checkbox";

const AccountLoginForm = ({ section, blockSettings }: { section: any, blockSettings?: any }) => {
    const loginForm = blockSettings?.loginForm || {};
    const registerCta = blockSettings?.registerCta || {};
    const newsletterSignup = blockSettings?.newsletterSignup || {};
    
    const {
        email_label = loginForm?.email_label || "Email address",
        email_placeholder = loginForm?.email_placeholder || "Email address",
        password_label = loginForm?.password_label || "Password",
        password_placeholder = loginForm?.password_placeholder || "Password",
        button_label_login = loginForm?.button_label_login || "Sign in",
        button_style_login = loginForm?.button_style_login || "primary",
        button_label_forgot_password = loginForm?.button_label_forgot_password || "Forgot your password?"
    } = section;

    // States
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [newsletterOptin, setNewsletterOptin] = useState(newsletterSignup?.newsletter_default_checked || false);

    // Hooks
    const { loginCustomer, loading: loginLoading, error, success } = useCustomerLogin();
    const {
        getAccessToken,
        loading: accessLoading,
        errorMessage: accessError,
        successMessage: accessSuccess
    } = useCustomerAccessToken();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Check if the pair of email and password exists in the system
        const data: any = await getAccessToken(email, password);

        // If exists, then sign in
        if (data.success) {
            await loginCustomer(email, password);
        }
    };

    const toggleForgotPassword = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setShowForgotPassword(!showForgotPassword);
    };

    const showNewsletterSignup = newsletterSignup?.show_newsletter_signup;
    const showRegisterCta = registerCta?.show_register_cta;
    const showSuccess = false;

    return (
        <>
            {!showForgotPassword && (
                <form
                    data-shopify-captcha='true'
                    id='loginForm'
                    onSubmit={handleSubmit}
                    className='account-page__form'
                >
                    <div className='flex flex-col gap-y-6'>
                        <p 
                            className={`w-full text-left mb-6 lg:mb-8 max-lg:text-center ${loginForm.title_login_style}`}
                            style={{ color: loginForm.header_color_schema }}
                        >
                            {loginForm?.title_login}
                        </p>

                        {(error || accessError) && (
                            <Paragraph
                                text={error || accessError}
                                type='b4'
                                classes='text-rhv-red'
                            />
                        )}

                        <TextField
                            id='emailLogin'
                            inputType='email'
                            type="floating-input" 
                            name='email'
                            value={email}
                            required={true}
                            label={`${email_label}*`}
                            placeholder={email_placeholder}
                            onChange={(e) => setEmail(e.target.value)}
                        />

                        <TextField
                            id='passwordLogin'
                            inputType='password'
                            type="floating-input" 
                            name='password'
                            value={password}
                            required={true}
                            label={`${password_label}*`}
                            placeholder={password_placeholder}
                            onChange={(e) => setPassword(e.target.value)}
                        />

                        {showNewsletterSignup && (
                            <Checkbox 
                                label={newsletterSignup?.newsletter_label || "Sign up for newsletter"} 
                                checked={newsletterOptin}
                                onChange={() => setNewsletterOptin(!newsletterOptin)}
                            />
                        )}

                        {showNewsletterSignup && newsletterSignup?.newsletter_disclaimer && (
                            <p className={`b4 account-page__form-terms text-[#696969]`}>
                                {newsletterSignup.newsletter_disclaimer}
                            </p>
                        )}

                        {showSuccess && (success || accessSuccess) && (
                            <Paragraph
                                text={success || accessSuccess}
                                type='success text-center'
                            />
                        )}

                        <Link
                            link='#'
                            onClick={toggleForgotPassword}
                            classes='w-full b4'
                            ariaLabel='Go to reset password page'
                        >
                            {button_label_forgot_password}
                        </Link>

                        <ButtonSpinner
                            isLoading={loginLoading || accessLoading}
                            classes={`button button-${button_style_login} w-full min-h-[60px] !text-lg`}
                            ariaLabel='Submit login form'
                            loadingText={'Logging in...'}
                        >
                            {button_label_login}
                        </ButtonSpinner>

                        {(showRegisterCta && section.layout !== "two_column_image") && (
                            <div className='text-center font-light w-full lg:w-[360px] py-[16px]'>
                                <div className='account-inquiry flex flex-col my-2 text-left space-y-[20px]'>
                                    <h4>{registerCta?.title_register_cta}</h4>

                                    <span className={`b4`}>
                                        {registerCta?.tagline_register_cta}
                                    </span>

                                    <a
                                        className={`button button-${registerCta?.button_style_register_cta || 'primary'} w-full  min-h-[60px] !text-lg block`}
                                        href='/account/register'
                                    >
                                        {registerCta?.button_label_register_cta || "Register"}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </form>
            )}

            {showForgotPassword && (
                <Suspense fallback={<div>Loading...</div>}>
                    <AccountForgotPassword
                        onCancel={toggleForgotPassword}
                        section={section}
                        blockSettings={blockSettings}
                    />
                </Suspense>
            )}
        </>
    );
};

export default AccountLoginForm;
